import { SubtreeController } from "subtree.js/core";
import type { AppDeps } from "../../app-deps";
import { loadReviewQueue } from "../../core/ops/review-ops";
import { isScanning } from "../../core/primitives/review-row";

export type InventoryDeps = Pick<AppDeps, "client" | "authHandler" | "storeRepository" | "reviewQueueRepository">;

export interface InventoryRouting {
  toReview(): void;
  toStock(): void;
}

/**
 * Inventory's way in, from the nav: Review when anything waits there, else
 * Stock, as iOS picks the pane the first time. A queue that cannot be read
 * opens Stock, which needs none.
 */
export class InventoryController extends SubtreeController {
  #decided = false;

  constructor(
    readonly deps: InventoryDeps,
    readonly routing: InventoryRouting,
    readonly now: () => Date = () => new Date(),
  ) {
    super();
    this.sync(() => {
      const shopId = deps.storeRepository.shopId;
      const queue = deps.reviewQueueRepository;
      if (this.#decided || shopId === null) return;
      if (queue.status.kind === "error") return this.#go(false);
      if (queue.status.kind === "loaded" && !queue.isStale) {
        return this.#go(queue.groups.some((g) => !isScanning(g.sample, this.now())));
      }
      if (queue.status.kind !== "loading")
        void loadReviewQueue({ ...deps, shopId, locationId: deps.storeRepository.locationId });
    }, [deps.storeRepository, deps.reviewQueueRepository]);
  }

  #go(review: boolean): void {
    if (this.isDisposed) return;
    this.#decided = true;
    if (review) this.routing.toReview();
    else this.routing.toStock();
  }
}
